/**
 * 장바구니 유틸리티 함수
 *
 * 옵션 비교, 아이템 중복 확인 등 장바구니 관련 유틸리티
 */

/**
 * 두 옵션 객체가 동일한지 비교
 *
 * JSON.stringify는 객체 키 순서에 의존하여 불안정한 비교가 발생할 수 있음
 * 이 함수는 키를 정렬하여 안정적인 비교를 수행
 *
 * @param options1 첫 번째 옵션 객체
 * @param options2 두 번째 옵션 객체
 * @returns 두 옵션이 동일하면 true
 */
export function areOptionsEqual(
  options1: Record<string, string[]> | undefined,
  options2: Record<string, string[]> | undefined
): boolean {
  // 둘 다 없으면 동일
  if (!options1 && !options2) return true;
  // 하나만 없으면 다름
  if (!options1 || !options2) return false;

  const keys1 = Object.keys(options1).sort();
  const keys2 = Object.keys(options2).sort();

  // 키 개수가 다르면 다름
  if (keys1.length !== keys2.length) return false;

  // 키가 다르면 다름
  if (keys1.join(',') !== keys2.join(',')) return false;

  // 각 키의 값 비교 (배열이므로 정렬 후 비교)
  return keys1.every((key) => {
    const arr1 = [...(options1[key] || [])].sort();
    const arr2 = [...(options2[key] || [])].sort();

    if (arr1.length !== arr2.length) return false;
    return arr1.every((val, idx) => val === arr2[idx]);
  });
}

/**
 * 두 커스터마이제이션 배열이 동일한지 비교
 *
 * @param customizations1 첫 번째 커스터마이제이션 배열
 * @param customizations2 두 번째 커스터마이제이션 배열
 * @returns 두 커스터마이제이션이 동일하면 true
 */
export function areCustomizationsEqual(
  customizations1: string[] | undefined,
  customizations2: string[] | undefined
): boolean {
  // 둘 다 없거나 빈 배열이면 동일
  if ((!customizations1 || customizations1.length === 0) &&
      (!customizations2 || customizations2.length === 0)) {
    return true;
  }

  // 하나만 없거나 빈 배열이면 다름
  if (!customizations1 || customizations1.length === 0) return false;
  if (!customizations2 || customizations2.length === 0) return false;

  // 길이가 다르면 다름
  if (customizations1.length !== customizations2.length) return false;

  // 정렬 후 비교
  const sorted1 = [...customizations1].sort();
  const sorted2 = [...customizations2].sort();

  return sorted1.every((val, idx) => val === sorted2[idx]);
}

/**
 * 메뉴 아이템 ID에서 순수 메뉴 ID 추출
 *
 * 장바구니 아이템 ID 형식: `{menuId}-{timestamp}`
 *
 * 타임스탬프 패턴: Date.now()로 생성된 밀리초 단위 타임스탬프 (13자리 숫자)
 * 예: "taco-123-1704067200000" → "taco-123"
 *
 * @param cartItemId 장바구니 아이템 ID
 * @returns 순수 메뉴 ID
 */
export function extractMenuId(cartItemId: string): string {
  // 마지막 하이픈 이후가 타임스탬프인 경우 제거
  const parts = cartItemId.split('-');
  if (parts.length > 1) {
    const lastPart = parts[parts.length - 1];
    // 타임스탬프 패턴 확인:
    // 1. 숫자로만 구성되어야 함
    // 2. 길이가 13자리여야 함 (Date.now()는 밀리초 단위라 13자리)
    // 3. 유효한 타임스탬프 범위여야 함 (2001년 ~ 2286년 사이)
    if (/^\d{13}$/.test(lastPart)) {
      const timestamp = parseInt(lastPart, 10);
      const minTimestamp = 1000000000000; // 2001-09-09
      const maxTimestamp = 9999999999999; // 2286-11-20
      if (timestamp >= minTimestamp && timestamp <= maxTimestamp) {
        return parts.slice(0, -1).join('-');
      }
    }
  }
  return cartItemId;
}
